import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

interface Student {
  name: string;
  department: string;
}

const STUDENTS_PER_PAGE = 25;

export default function Epityhontes() {
  const [students, setStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/assets/epityhontes.csv')
      .then(response => response.text())
      .then(text => {
        const lines = text.split('\n').filter(line => line.trim());
        // Skip header row, parse remaining lines
        const parsed = lines.slice(1).map(line => {
          const parts = line.split(';');
          return {
            name: parts[1]?.trim() || '',
            department: parts[2]?.trim() || ''
          };
        }).filter(s => s.name && s.department);
        setStudents(parsed);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load students:', err);
        setLoading(false);
      });
  }, []);

  // Filter students by search query
  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return students;
    const query = searchQuery.toLowerCase();
    return students.filter(s =>
      s.name.toLowerCase().includes(query) ||
      s.department.toLowerCase().includes(query)
    );
  }, [students, searchQuery]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / STUDENTS_PER_PAGE);
  const startIndex = (currentPage - 1) * STUDENTS_PER_PAGE;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + STUDENTS_PER_PAGE);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  return (
    <>
      <section className="page-hero">
        <div className="wrap">
          <Link to="/" className="back-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Αρχική
          </Link>
          <p></p>
          <p className="eyebrow paper">Πανελλήνιες Εξετάσεις</p>
          <h1>Οι επιτυχόντες μας</h1>
          <p className="page-sub">Μαθητές που πέτυχαν την εισαγωγή τους σε ΑΕΙ μέσω του φροντιστηρίου μας.</p>
        </div>
      </section>

      <section className="sec results-section">
        <div className="wrap">
          {/* Search input */}
          <div className="results-search">
            <input
              type="text"
              placeholder="Αναζήτηση ονόματος ή σχολής..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Results count */}
          <div className="results-count">
            {loading ? (
              'Φόρτωση...'
            ) : (
              <>
                <strong>{filteredStudents.length}</strong> {filteredStudents.length === 1 ? 'αποτέλεσμα' : 'αποτελέσματα'}
              </>
            )}
          </div>

          <div className="results-table-wrap">
            <table className="results-table">
              <thead>
                <tr>
                  <th>Όνομα</th>
                  <th>Σχολή</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: 'center' }}>Φόρτωση...</td>
                  </tr>
                ) : paginatedStudents.length === 0 ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: 'center' }}>Δεν βρέθηκαν αποτελέσματα</td>
                  </tr>
                ) : (
                  paginatedStudents.map((student, index) => (
                    <tr key={startIndex + index}>
                      <td data-label="Όνομα">{student.name}</td>
                      <td data-label="Σχολή">{student.department}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Προηγ.
              </button>

              {getPageNumbers().map((page, idx) => (
                typeof page === 'number' ? (
                  <button
                    key={idx}
                    className={currentPage === page ? 'active' : ''}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ) : (
                  <span key={idx} className="pagination-ellipsis">{page}</span>
                )
              ))}

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Επόμ.
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
